import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  UserPlus,
  Receipt,
  UserCheck,
  MessageCircle,
  CornerDownLeft,
  LayoutDashboard,
  Users,
  Ticket,
  Dumbbell,
  CreditCard,
  BarChart3,
  Bell,
  Settings,
  HeartPulse,
  Filter,
  Building2,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import type { MemberSummary } from '@techsofit/api-types';
import { cn, StatusBadge, useConfirm } from '@techsofit/ui';
import { listMembers } from '../lib/gymApi';
import { waDigits } from '../lib/format';
import { statusToBadge } from '../lib/membership';
import { staffCheckIn } from '../lib/staffCheckIn';

/** Static destinations the palette can jump to. Ordered by how often the front desk needs them. */
const PAGES: { label: string; to: string; icon: LucideIcon }[] = [
  { label: 'Dashboard', to: '/dashboard', icon: LayoutDashboard },
  { label: 'Members', to: '/members', icon: Users },
  { label: 'Leads', to: '/leads', icon: Filter },
  { label: 'Corporate', to: '/corporate', icon: Building2 },
  { label: 'Member health', to: '/health', icon: HeartPulse },
  { label: 'Payments', to: '/payments', icon: Receipt },
  { label: 'Finance', to: '/finance', icon: Wallet },
  { label: 'Attendance', to: '/attendance', icon: UserCheck },
  { label: 'Guests', to: '/guests', icon: Ticket },
  { label: 'Workouts', to: '/workouts', icon: Dumbbell },
  { label: 'Plans', to: '/plans', icon: CreditCard },
  { label: 'Notifications', to: '/notifications', icon: Bell },
  { label: 'Reports', to: '/reports', icon: BarChart3 },
  { label: 'Settings', to: '/settings', icon: Settings },
];

/** The receptionist's daily verbs — always available, filtered by the query like everything else. */
const ACTIONS: { label: string; to: string; icon: LucideIcon }[] = [
  { label: 'New member', to: '/members/new', icon: UserPlus },
  { label: 'Record payment', to: '/payments/new', icon: Receipt },
  { label: 'Check-in register', to: '/attendance', icon: UserCheck },
];

type Item =
  | { kind: 'member'; key: string; member: MemberSummary }
  | { kind: 'nav'; key: string; label: string; to: string; icon: LucideIcon }
  | { kind: 'action'; key: string; label: string; to: string; icon: LucideIcon };

/**
 * Global command palette (⌘K / Ctrl+K). Search a member and jump to them or fire a quick action
 * (record payment, check in, WhatsApp), or jump to any page. Member search is server-side via
 * {@link listMembers}; pages and actions filter client-side on the same query.
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [query, setQuery] = useState('');
  const [members, setMembers] = useState<MemberSummary[]>([]);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset each time it opens so it always starts clean and focused.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setMembers([]);
    setHighlight(0);
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      cancelAnimationFrame(id);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  // Debounced server-side member search. Empty query → no members (pages/actions still show).
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (!q) {
      setMembers([]);
      return;
    }
    let alive = true;
    const t = setTimeout(() => {
      listMembers({ query: q, size: 6, active: true })
        .then((page) => alive && setMembers(page.items))
        .catch(() => alive && setMembers([]));
    }, 150);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [query, open]);

  const q = query.trim().toLowerCase();
  const items = useMemo<Item[]>(() => {
    const memberItems: Item[] = members.map((m) => ({ kind: 'member', key: `m:${m.id}`, member: m }));
    const match = (label: string) => !q || label.toLowerCase().includes(q);
    const actionItems: Item[] = ACTIONS.filter((a) => match(a.label)).map((a) => ({
      kind: 'action',
      key: `a:${a.to}`,
      ...a,
    }));
    const navItems: Item[] = PAGES.filter((p) => match(p.label)).map((p) => ({
      kind: 'nav',
      key: `n:${p.to}`,
      ...p,
    }));
    return [...memberItems, ...actionItems, ...navItems];
  }, [members, q]);

  // Keep the highlight in range as the result set changes under the cursor.
  useEffect(() => {
    setHighlight((h) => Math.min(h, Math.max(0, items.length - 1)));
  }, [items.length]);

  function close() {
    onClose();
  }

  function go(to: string, state?: Record<string, unknown>) {
    close();
    navigate(to, state ? { state } : undefined);
  }

  async function checkIn(m: MemberSummary) {
    close();
    try {
      if (!(await staffCheckIn(m, confirm))) return;
    } catch {
      return;
    }
    navigate('/attendance');
  }

  function whatsapp(m: MemberSummary) {
    const num = waDigits(m.whatsappPhone ?? m.phone);
    if (num) window.open(`https://wa.me/${num}`, '_blank', 'noopener');
    close();
  }

  function activate(item: Item) {
    if (item.kind === 'member') return go(`/members/${item.member.id}`);
    return go(item.to);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = items[highlight];
      if (item) activate(item);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  }

  // Scroll the highlighted row into view when navigating by keyboard.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${highlight}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  if (!open) return null;

  const firstNavIdx = items.findIndex((it) => it.kind === 'nav');
  const firstActionIdx = items.findIndex((it) => it.kind === 'action');

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 sm:pt-[12vh]" role="dialog" aria-modal="true" aria-label="Search">
      <button type="button" aria-label="Close search" onClick={close} className="absolute inset-0 bg-ink/40 backdrop-blur-sm animate-fade-in" />
      <div className="relative flex max-h-[76vh] w-full max-w-xl flex-col overflow-hidden rounded-card bg-card shadow-pop animate-pop-in">
        {/* Search field */}
        <div className="flex items-center gap-3 border-b border-line px-4">
          <Search className="size-5 shrink-0 text-muted" aria-hidden />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlight(0);
            }}
            onKeyDown={onKeyDown}
            type="text"
            placeholder="Search members, pages, actions…"
            aria-label="Search members, pages and actions"
            className="h-14 w-full bg-transparent text-ink placeholder:text-muted focus:outline-none"
          />
          <kbd className="hidden shrink-0 rounded border border-line px-1.5 py-0.5 text-[11px] text-muted sm:block">esc</kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto p-1.5">
          {items.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-muted">
              {q ? `No matches for “${query.trim()}”.` : 'Type to search.'}
            </p>
          ) : (
            <>
              {members.length > 0 && <SectionLabel>Members</SectionLabel>}
              {items.map((item, idx) => (
                <div key={item.key}>
                  {idx === firstActionIdx && <SectionLabel>Quick actions</SectionLabel>}
                  {idx === firstNavIdx && <SectionLabel>Go to</SectionLabel>}
                  {item.kind === 'member' ? (
                    <MemberRow
                      member={item.member}
                      active={idx === highlight}
                      idx={idx}
                      onHover={() => setHighlight(idx)}
                      onOpen={() => activate(item)}
                      onPay={() => go('/payments/new', { memberId: item.member.id })}
                      onCheckIn={() => checkIn(item.member)}
                      onWhatsApp={() => whatsapp(item.member)}
                    />
                  ) : (
                    <CommandRow
                      icon={item.icon}
                      label={item.label}
                      active={idx === highlight}
                      idx={idx}
                      onHover={() => setHighlight(idx)}
                      onClick={() => activate(item)}
                    />
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted">{children}</p>;
}

function CommandRow({
  icon: Icon,
  label,
  active,
  idx,
  onHover,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  active: boolean;
  idx: number;
  onHover: () => void;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-idx={idx}
      onMouseMove={onHover}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 rounded-control px-3 py-2.5 text-left text-sm',
        active ? 'bg-surface text-ink' : 'text-ink-soft',
      )}
    >
      <Icon className="size-4 shrink-0 text-muted" aria-hidden />
      <span className="flex-1 truncate font-medium">{label}</span>
      {active && <CornerDownLeft className="size-3.5 shrink-0 text-muted" aria-hidden />}
    </button>
  );
}

function MemberRow({
  member,
  active,
  idx,
  onHover,
  onOpen,
  onPay,
  onCheckIn,
  onWhatsApp,
}: {
  member: MemberSummary;
  active: boolean;
  idx: number;
  onHover: () => void;
  onOpen: () => void;
  onPay: () => void;
  onCheckIn: () => void;
  onWhatsApp: () => void;
}) {
  const hasWhatsApp = !!(member.whatsappPhone ?? member.phone);
  return (
    <div
      data-idx={idx}
      onMouseMove={onHover}
      className={cn('flex items-center gap-2 rounded-control pr-2', active ? 'bg-surface' : '')}
    >
      <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-left">
        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-dark text-xs font-semibold text-on-dark">
          {member.fullName.trim().charAt(0).toUpperCase()}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-ink">{member.fullName}</span>
          <span className="block truncate text-xs text-muted tabular">{member.phone}</span>
        </span>
        <span className="shrink-0">
          <StatusBadge status={statusToBadge(member.status)} />
        </span>
      </button>
      {/* Inline quick actions for this member — the palette's whole point at the front desk. */}
      <div className="flex shrink-0 items-center gap-0.5">
        <IconAction label={`Record payment for ${member.fullName}`} onClick={onPay}>
          <Receipt className="size-4" aria-hidden />
        </IconAction>
        <IconAction label={`Check in ${member.fullName}`} onClick={onCheckIn}>
          <UserCheck className="size-4" aria-hidden />
        </IconAction>
        {hasWhatsApp && (
          <IconAction label={`WhatsApp ${member.fullName}`} onClick={onWhatsApp}>
            <MessageCircle className="size-4" aria-hidden />
          </IconAction>
        )}
      </div>
    </div>
  );
}

function IconAction({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className="inline-flex size-8 items-center justify-center rounded-control text-muted transition-colors hover:bg-card hover:text-ink"
    >
      {children}
    </button>
  );
}
